import { Link, useLocation, useNavigate } from "react-router-dom";
import { ClipboardList, FileText, LogOut, Search, ShieldCheck, ShoppingBag, User } from "lucide-react";
import { Brand } from "@/components/common/Brand";
import { Button } from "@/components/ui/button";
import { NotificationBell } from "@/components/common/NotificationBell";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useAuth } from "@/context/auth-context";
import { useCart } from "@/context/cart-context";
import { cn } from "@/lib/utils";

const LINKS = [
  { to: "/", label: "Browse", icon: ShoppingBag, key: "browse", end: true },
  { to: "/search", label: "Search", icon: Search, key: "search" },
  { to: "/orders", label: "Orders", icon: ClipboardList, key: "orders" },
];

export function CustomerShell({ children }) {
  const { user, signOut } = useAuth();
  const { count } = useCart();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 border-b bg-background/80 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center gap-4 px-4 py-3 lg:px-6">
          <Link to="/" data-testid="home-link">
            <Brand />
          </Link>
          <nav className="ml-4 hidden items-center gap-1 md:flex">
            {LINKS.map((l) => {
              const active = l.end ? pathname === l.to : pathname.startsWith(l.to);
              return (
                <Link
                  key={l.key}
                  to={l.to}
                  data-testid={`nav-${l.key}`}
                  className={cn(
                    "rounded-full px-3.5 py-2 text-sm font-medium transition-colors",
                    active ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {l.label}
                </Link>
              );
            })}
          </nav>
          <div className="ml-auto flex items-center gap-1.5">
            <Button
              variant="ghost"
              size="icon"
              className="relative"
              onClick={() => navigate("/cart")}
              data-testid="cart-button"
            >
              <ShoppingBag className="h-5 w-5" />
              {count > 0 && (
                <span className="absolute right-0.5 top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] text-primary-foreground">
                  {count}
                </span>
              )}
            </Button>
            <NotificationBell />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" data-testid="profile-menu-trigger">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-secondary text-xs text-secondary-foreground">
                      {(user?.name || "?").slice(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <div className="px-2 py-1.5">
                  <p className="truncate text-sm">{user?.name}</p>
                  <p className="truncate text-xs text-muted-foreground">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/orders")} data-testid="menu-orders">
                  <ClipboardList className="mr-2 h-4 w-4" /> My orders
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/become-vendor")} data-testid="menu-become-vendor">
                  <User className="mr-2 h-4 w-4" /> Open a store
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/privacy")} data-testid="menu-privacy">
                  <ShieldCheck className="mr-2 h-4 w-4" /> Privacy & data
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/legal/policy")} data-testid="menu-legal">
                  <FileText className="mr-2 h-4 w-4" /> Legal
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => signOut().then(() => navigate("/login"))}
                  data-testid="menu-sign-out"
                >
                  <LogOut className="mr-2 h-4 w-4" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        <div className="checker checker-red checker-sm h-2 w-full" />
      </header>
      <main className="mx-auto max-w-6xl px-4 pb-24 pt-6 lg:px-6">{children}</main>
    </div>
  );
}
